import React, { useEffect, useState } from "react";
import { ArrowRight, Scale, BarChart3, Landmark, KeyRound, MapPin, Map, X } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { blogApi } from "../../api";

const icons = { scale: Scale, "bar-chart-3": BarChart3, landmark: Landmark, "key-round": KeyRound, "map-pin": MapPin, map: Map };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };

function ArticleModal({ post, onClose }) {
  const Icon = icons[post.icon] || BarChart3;
  return (
    <div onClick={onClose} data-testid="blog-article-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)", zIndex: 250, display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem" }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ background: "#0f0e13", width: "100%", maxWidth: 680, maxHeight: "85vh", overflowY: "auto", padding: "2.6rem", position: "relative", borderColor: "rgba(201,162,75,0.2)" }}>
        <button onClick={onClose} data-testid="close-article" style={{ position: "absolute", top: 18, right: 18, background: "none", border: "none", color: "var(--muted)", cursor: "pointer" }}><X size={20} /></button>
        <div style={{ display: "flex", alignItems: "center", gap: "0.7rem", marginBottom: "1rem" }}>
          <span style={{ color: "var(--gold)" }}><Icon size={18} /></span>
          <span className="eyebrow" style={{ fontSize: "0.62rem", color: "var(--gold)" }}>{post.tag || "Market Update"}</span>
        </div>
        <h2 className="font-serif" style={{ fontSize: "1.9rem", lineHeight: 1.15, marginBottom: "0.6rem", color: "var(--text)" }}>{post.title}</h2>
        <div style={{ fontSize: "0.75rem", color: "var(--muted-2)", marginBottom: "1.6rem" }}>{fmtDate(post.published_at || post.created_at)}</div>
        <div className="blog-body" style={{ color: "var(--muted)", fontSize: "0.95rem", lineHeight: 1.8 }} dangerouslySetInnerHTML={{ __html: post.body_html }} />
      </div>
    </div>
  );
}

export default function Blog() {
  const { c } = useLang();
  const b = c.blog;
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { blogApi.posts().then(setPosts).catch(() => setPosts([])); }, []);

  const hasLive = posts.length > 0;

  return (
    <section id="blog" className="section">
      <div className="container-x">
        <div className="reveal" style={{ maxWidth: 720, marginBottom: "3.5rem" }}>
          <span className="eyebrow">{b.eyebrow}</span>
          <h2 className="display" style={{ fontSize: "clamp(2.2rem,5vw,4rem)", margin: "1.2rem 0 1.2rem" }}>
            {b.title.pre}<span className="italic-gold">{b.title.em}</span>{b.title.post}
          </h2>
          <p className="body-text">{b.para}</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1.5rem" }}>
          {hasLive ? posts.map((p) => {
            const Icon = icons[p.icon] || BarChart3;
            return (
              <button key={p.id} onClick={() => setActive(p)} data-testid={`blog-card-${p.id}`} className="card" style={{ padding: "2rem", textAlign: "left", cursor: "pointer", background: "transparent", display: "flex", flexDirection: "column", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.8rem", marginBottom: "1.2rem" }}>
                  <span style={{ color: "var(--gold)" }}><Icon size={20} /></span>
                  <span className="eyebrow" style={{ fontSize: "0.62rem", color: "var(--gold)" }}>{p.tag}</span>
                </div>
                <h3 className="font-serif" style={{ fontSize: "1.4rem", marginBottom: "0.8rem", color: "var(--text)", lineHeight: 1.15 }}>{p.title}</h3>
                <p className="body-text" style={{ fontSize: "0.88rem", flex: 1, marginBottom: "1.4rem" }}>{p.excerpt}</p>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "1rem" }}>
                  <span style={{ fontSize: "0.75rem", color: "var(--muted-2)" }}>{fmtDate(p.published_at || p.created_at)}</span>
                  <span style={{ color: "var(--gold)", display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem" }}>{b.read} <ArrowRight size={13} /></span>
                </div>
              </button>
            );
          }) : b.posts.map((p, i) => {
            const Icon = icons[p.icon] || Scale;
            return (
              <div key={i} className="card" style={{ padding: "2rem", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.8rem", marginBottom: "1.2rem" }}>
                  <span style={{ color: "var(--gold)" }}><Icon size={20} /></span>
                  <span className="eyebrow" style={{ fontSize: "0.62rem", color: "var(--gold)" }}>{p.tag}</span>
                </div>
                <h3 className="font-serif" style={{ fontSize: "1.4rem", marginBottom: "0.8rem", color: "var(--text)", lineHeight: 1.15 }}>{p.title}</h3>
                <p className="body-text" style={{ fontSize: "0.88rem", flex: 1, marginBottom: "1.4rem" }}>{p.excerpt}</p>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "1rem" }}>
                  <span style={{ fontSize: "0.75rem", color: "var(--muted-2)" }}>{p.date} · {p.read}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {active && <ArticleModal post={active} onClose={() => setActive(null)} />}
    </section>
  );
}
